// Gumroad entitlement transfer: recipient email, policy agreement, transfer action

import { useCallback, useState } from 'react';
import { RevoltButton, RevoltInput, RevoltSwitch } from '../../../components';
import { useManager } from '../../../core';
import { AccountManager } from '../managers/account-manager';
import { TRANSFER_SOURCE_KIND } from '../transferSources';
import { TransferPolicyModal } from './TransferPolicyModal';

function recipientInvalidMessage(code) {
  if (code === 'self') {
    return 'You cannot transfer to your own account.';
  }
  if (code === 'already_has_local') {
    return 'The recipient already has the Local plan, no transfer is needed.';
  }
  if (code === 'already_transferred_pair') {
    return 'This entitlement was already transferred or redeemed between these accounts.';
  }
  return 'No Revolt account uses that email. Double check the address, or the recipient may need to sign up first.';
}

/** ISO date from API → readable local date and time (e.g. "Thu, Apr 30, 2025, 3:08 PM"). */
function formatFriendlyDateTime(isoString) {
  if (!isoString || typeof isoString !== 'string') return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return String(isoString);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function senderPhaseHint(phase) {
  if (phase === 'after_claim') {
    return 'Revolt Local is on this account already. A completed transfer may remove it from your login when that step runs.';
  }
  if (phase === 'before_claim') {
    return 'Revolt Local is not on this account yet. You are handing eligibility to the recipient before you activate Local for yourself.';
  }
  return null;
}

function transferExecuteErrorUserMessage(status, data) {
  const code = data?.error;
  if (code === 'already_transferred_pair' || status === 409) {
    return (
      data?.message ||
      'This entitlement was already transferred or redeemed between these accounts.'
    );
  }
  if (code === 'test_mode_requires_admin') {
    return 'Test mode is only available to administrators.';
  }
  if (code === 'unsupported_source') {
    return 'This transfer source is not supported.';
  }
  if (data?.simulation?.summary && typeof data.simulation.summary === 'string') {
    return data.simulation.summary;
  }
  if (typeof data?.message === 'string' && data.message) {
    return data.message;
  }
  return 'The transfer could not be completed. Please try again or contact support.';
}

export function TransferSourceDetailGumroad({ source, transferAdminTestMode = false }) {
  const { manager: accountManager } = useManager(AccountManager);
  const [emailInput, setEmailInput] = useState('');
  const [lockedEmail, setLockedEmail] = useState(null);
  const [senderTransferPhase, setSenderTransferPhase] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [lookupWarning, setLookupWarning] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferExecuteError, setTransferExecuteError] = useState(null);
  const [transferSuccess, setTransferSuccess] = useState(null);

  const submitEmail = useCallback(async () => {
    setLookupError(null);
    setLookupWarning(null);
    setSenderTransferPhase(null);
    setTransferSuccess(null);
    setTransferExecuteError(null);
    const trimmed = emailInput.trim();
    if (!trimmed) {
      setLookupError('Enter an email address.');
      return;
    }
    const basic = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basic.test(trimmed)) {
      setLookupError('Enter a valid email address.');
      return;
    }
    if (!accountManager) return;

    const bypass = transferAdminTestMode;
    const normalizedEmail = trimmed.toLowerCase();

    setLookupLoading(true);
    try {
      const result = await accountManager.checkTransferRecipient(trimmed);

      if (!result.success) {
        if (bypass) {
          const apiHint =
            result.error === 'invalid_email'
              ? 'Invalid email per server check.'
              : 'Recipient could not be verified with the server.';
          setLookupWarning(`${apiHint} (admin test: continuing anyway.)`);
          setLockedEmail(normalizedEmail);
          setSenderTransferPhase(null);
          setPolicyAccepted(false);
          return;
        }
        if (result.error === 'invalid_email') {
          setLookupError('Enter a valid email address.');
        } else {
          setLookupError('Could not verify that email. Try again.');
        }
        return;
      }

      if (!result.valid) {
        const msg = recipientInvalidMessage(result.code);
        if (bypass) {
          setLookupWarning(`${msg} (admin test: continuing anyway.)`);
          setLockedEmail(normalizedEmail);
          setSenderTransferPhase(null);
          setPolicyAccepted(false);
          return;
        }
        setLookupError(msg);
        return;
      }

      setLockedEmail(normalizedEmail);
      setSenderTransferPhase(result.senderTransferPhase ?? null);
      setPolicyAccepted(false);
    } finally {
      setLookupLoading(false);
    }
  }, [accountManager, emailInput, transferAdminTestMode]);

  const handleTransfer = async () => {
    if (!accountManager || !lockedEmail) return;
    setTransferExecuteError(null);
    setTransferSubmitting(true);
    const testMode = Boolean(transferAdminTestMode);
    try {
      const { ok, status, data } = await accountManager.executeGumroadUePluginTransfer({
        recipientEmail: lockedEmail,
        testMode,
        sourceId: source.id,
      });

      if (testMode) {
        const printable = JSON.stringify({ ok, status, data }, null, 2);
        console.log(
          '[transfer] Gumroad UE transfer simulation (admin test mode, no DB changes)\n' +
            printable
        );
        if (!ok || !data?.success) {
          setTransferExecuteError(transferExecuteErrorUserMessage(status, data || {}));
          return;
        }
        if (!data.simulation?.wouldSucceed) {
          setTransferExecuteError(
            typeof data.simulation?.summary === 'string' && data.simulation.summary
              ? data.simulation.summary
              : 'This transfer would be blocked (see server checks).'
          );
          return;
        }
        setTransferSuccess({
          kind: 'dryRun',
          simulation: data.simulation,
          recipientEmail: lockedEmail,
        });
        return;
      }

      if (ok && data?.success) {
        setTransferSuccess({
          kind: 'live',
          simulation: data.simulation,
          entitlementTransfer: data.entitlementTransfer,
          executed: data.executed,
          message: data.message,
        });
        return;
      }

      setTransferExecuteError(transferExecuteErrorUserMessage(status, data || {}));
    } catch (e) {
      setTransferExecuteError(e?.message || 'The transfer could not be completed.');
    } finally {
      setTransferSubmitting(false);
    }
  };

  if (source.kind !== TRANSFER_SOURCE_KIND.GUMROAD) return null;

  const phaseHint = senderPhaseHint(senderTransferPhase);
  const transferDone = Boolean(transferSuccess);
  const successClassName =
    transferSuccess?.kind === 'dryRun'
      ? 'gumroad-transfer-success gumroad-transfer-success--dry-run'
      : 'gumroad-transfer-success';

  return (
    <div className="transfer-detail-body transfer-detail-body--gumroad">
      <div className="gumroad-transfer-form">
        <div className="gumroad-transfer-email-block">
          {!lockedEmail ? (
            <>
              <p className="gumroad-transfer-hint">
                Enter the Revolt account email of the person receiving this entitlement.
              </p>
              <form
                className="gumroad-transfer-email-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitEmail();
                }}
              >
                <RevoltInput
                  type="email"
                  autoComplete="email"
                  placeholder="recipient@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  disabled={lookupLoading}
                  className="gumroad-transfer-email-input"
                />
                <RevoltButton type="submit" variant="primary" disabled={lookupLoading}>
                  {lookupLoading ? 'Checking…' : 'Continue'}
                </RevoltButton>
              </form>
            </>
          ) : (
            <div className="gumroad-transfer-email-locked">
              <span className="gumroad-transfer-email-locked-label">Recipient</span>
              <span className="gumroad-transfer-email-locked-value">{lockedEmail}</span>
            </div>
          )}
          {phaseHint && !transferDone && (
            <p className="gumroad-transfer-phase-hint" role="note">
              {phaseHint}
            </p>
          )}
          {lookupWarning && !transferDone && (
            <div className="gumroad-transfer-warning" role="status">
              {lookupWarning}
            </div>
          )}
          {lookupError && (
            <div className="gumroad-transfer-error" role="alert">
              {lookupError}
            </div>
          )}
        </div>

        {transferSuccess && (
          <div className={successClassName} role="status">
            {transferSuccess.kind === 'dryRun' ? (
              <>
                <h3 className="gumroad-transfer-success-title">Test transfer finished (admin)</h3>
                <p className="gumroad-transfer-success-lead">
                  Nothing was saved. This was a preview only.
                </p>
                <dl className="gumroad-transfer-success-dl">
                  <div>
                    <dt>Recipient</dt>
                    <dd>{transferSuccess.recipientEmail}</dd>
                  </div>
                </dl>
                <p className="gumroad-transfer-success-foot">
                  After a real transfer, the recipient should DM <strong>SpicyMelon</strong> on Discord
                  for <strong>100% off</strong> promotion code.
                </p>
              </>
            ) : (
              <>
                <h3 className="gumroad-transfer-success-title">Transfer successful</h3>
                <p className="gumroad-transfer-success-lead">Your Gumroad transfer is complete.</p>
                <dl className="gumroad-transfer-success-dl">
                  {transferSuccess.entitlementTransfer?.transferId && (
                    <div>
                      <dt>Confirmation number</dt>
                      <dd className="gumroad-transfer-mono">
                        {transferSuccess.entitlementTransfer.transferId}
                      </dd>
                    </div>
                  )}
                  {transferSuccess.entitlementTransfer?.createdAt && (
                    <div>
                      <dt>Submitted</dt>
                      <dd>{formatFriendlyDateTime(transferSuccess.entitlementTransfer.createdAt)}</dd>
                    </div>
                  )}
                </dl>
                <p className="gumroad-transfer-success-foot">
                  The recipient should DM <strong>SpicyMelon</strong> on Discord for{' '}
                  <strong>100% off</strong> promotion code for the <strong>Revolt Lifetime Plan Bundle</strong>. Include your confirmation number when they
                  reach out.
                </p>
              </>
            )}
          </div>
        )}

        {lockedEmail && !transferDone && (
          <>
            <div className="gumroad-transfer-policy-block">
              <div className="gumroad-transfer-policy-row">
                <RevoltSwitch
                  id="gumroad-transfer-policy"
                  checked={policyAccepted}
                  onChange={setPolicyAccepted}
                  disabled={transferSubmitting}
                />
                <div className="gumroad-transfer-policy-copy">
                  <span>I agree to the </span>
                  <button
                    type="button"
                    className="gumroad-transfer-policy-link"
                    onClick={() => setPolicyModalOpen(true)}
                  >
                    Transfer Policy
                  </button>
                  <span>.</span>
                </div>
              </div>
            </div>

            {transferExecuteError && (
              <div className="gumroad-transfer-error" role="alert">
                {transferExecuteError}
              </div>
            )}

            <div className="gumroad-transfer-actions">
              <RevoltButton
                variant="primary"
                disabled={!policyAccepted || transferSubmitting}
                onClick={handleTransfer}
              >
                {transferSubmitting ? 'Submitting…' : 'Transfer'}
              </RevoltButton>
            </div>
          </>
        )}
      </div>

      <TransferPolicyModal isOpen={policyModalOpen} onClose={() => setPolicyModalOpen(false)} />
    </div>
  );
}
